import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MoneyUtils from '../../../utils/moneyUtils';
import { useRestaurantContext } from '../../../context/RestaurantContext';
import { useCartContext } from '../../../context/CartContext';
import Icon from '../../../components/AppIcon';


// Inline chevron icon
const ChevronDown = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const MenuItemCard = ({ item, onItemClick, isGridView, onOpenCart }) => {
  const { theme, selfOrderConfig, capabilities } = useRestaurantContext();
  const { cartData, addItem, updateQuantity, removeItem } = useCartContext();

  const isOrderingEnabled = capabilities?.isOrderingEnabled ?? true;
  const [isOpen, setIsOpen] = useState(false);

  // ── Offer / price ─────────────────────────────────────────────────
  const showOffer =
    typeof item?.price === 'number' &&
    typeof item?.offerPrice === 'number' &&
    item.offerPrice < item.price;

  const discount = showOffer
    ? Math.round(((item.price - (item?.offerPrice ?? 0)) / item.price) * 100)
    : null;

  const hasPortions = item.portions && item.portions.length > 0;
  const hasAddons = (item.addons && item.addons.length > 0) || (item.addOns && item.addOns.length > 0);
  const hasPortionsOrAddons = hasPortions || hasAddons || item.isCombo;
  const displayPrice = showOffer ? item.offerPrice : item.price;

  // ── Cart state ────────────────────────────────────────────────────
  const cartItems = cartData?.items || [];
  const cartEntriesForItem = cartItems.filter(
    (ci) => ci.id === item.id || ci._id === item.id
  );
  const totalQtyInCart = cartEntriesForItem.reduce(
    (sum, ci) => sum + (ci.quantity || 0), 0
  );
  // simpleCartItem: only used when there's exactly ONE entry and no addons/portions
  const simpleCartItem = (!hasPortionsOrAddons && cartEntriesForItem.length === 1) ? cartEntriesForItem[0] : null;

  // ── Handlers ──────────────────────────────────────────────────────
  const handleCardClick = (e) => {
    e.stopPropagation();
    if (item?.isAvailable === false) return;
    onItemClick(item);
  };

  const handleAdd = (e) => {
    e.stopPropagation();
    // Route through onItemClick which contains all smart routing logic
    onItemClick(item);
  };

  const handleIncrement = (e) => {
    e.stopPropagation();
    if (hasPortionsOrAddons) {
      // Has customizations — route through smart handler (may open repeat sheet)
      onItemClick(item);
      return;
    }
    if (!simpleCartItem) {
      onItemClick(item);
      return;
    }
    updateQuantity(simpleCartItem.cartItemId, simpleCartItem.quantity + 1);
  };

  const handleDecrement = async (e) => {
    e.stopPropagation();
    if (hasPortionsOrAddons) {
      // Has customizations — open repeat sheet to manage
      onItemClick(item);
      return;
    }
    if (!simpleCartItem) return;
    if (simpleCartItem.quantity <= 1) {
      await removeItem(simpleCartItem.cartItemId);
    } else {
      await updateQuantity(simpleCartItem.cartItemId, simpleCartItem.quantity - 1);
    }
  };

  const themeColor = theme?.secondary || '#000';
  const themeTextColor = theme?.primary || '#fff';

  // ── Action widget (right side of card) ───────────────────────────
  const ActionWidget = () => {
    if (!isOrderingEnabled) {
      return null;
    }

    const isOutOfStock = item?.isAvailable === false;

    if (isOutOfStock) {
      return (
        <div className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-100 text-gray-400 select-none cursor-not-allowed">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    }

    if (totalQtyInCart === 0) {
      // Show simple + button — routes through smart handler
      return (
        <motion.div
          whileTap={{ scale: 0.85 }}
          onClick={(e) => { e.stopPropagation(); onItemClick(item); }}
          className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-base cursor-pointer select-none"
          style={{ backgroundColor: themeColor, color: themeTextColor }}
        >
          +
        </motion.div>
      );
    }

    if (totalQtyInCart > 0) {
      return (
        <div
          className="flex items-center gap-1 rounded-full px-1 py-0.5 border bg-white shadow-sm"
          style={{ borderColor: themeColor }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleDecrement}
            className="w-5 h-5 flex items-center justify-center rounded-full text-sm font-bold animate-active"
            style={{ color: themeColor }}
          >
            −
          </button>
          <span
            className="text-xs font-bold min-w-[14px] text-center"
            style={{ color: themeColor }}
          >
            {totalQtyInCart}
          </span>
          <button
            onClick={handleIncrement}
            className="w-5 h-5 flex items-center justify-center rounded-full text-sm font-bold text-white"
            style={{ backgroundColor: themeColor, color: themeTextColor }}
          >
            +
          </button>
        </div>
      );
    }

    return null;
  };

  if (isGridView) {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="bg-white rounded-2xl shadow-sm overflow-hidden text-[12px] flex flex-col justify-between h-full border border-gray-100 hover:shadow-md transition-shadow relative"
      >
        <div onClick={handleCardClick} className="cursor-pointer flex flex-col h-full">
          {/* Image on top */}
          <div className="relative w-full h-24 bg-gray-50 flex-shrink-0 overflow-hidden">
            {item?.imageUrl ? (
              <img
                src={item.imageUrl}
                alt={item?.name || 'Menu item'}
                className={`w-full h-full object-cover absolute inset-0 transition-opacity duration-300 ${item?.isAvailable === false ? 'opacity-40 grayscale-[0.5]' : 'opacity-100'}`}
              />
            ) : (
              <div className="absolute inset-0 bg-gray-50 flex items-center justify-center text-gray-300">
                <Icon 
                  name={/(drink|beverage|juice|coffee|tea|shake|smoothie|water)/i.test(item?.category || item?.name) ? 'Coffee' : 'Utensils'} 
                  size={20} 
                  strokeWidth={1.5}
                />
              </div>
            )}
            
            {/* Combo Ribbon Banner */}
            {item?.isCombo && (
                <div className="absolute top-0 left-0 bg-black text-white text-[8px] font-bold px-1.5 py-0.5 rounded-br-lg shadow-sm z-10">
                    COMBO
                </div>
            )}
            
            {/* Veg / Non-Veg badge on top of image */}
            <div className="absolute top-1.5 right-1.5 bg-white/90 backdrop-blur-sm p-0.5 rounded-md shadow-sm z-10 flex items-center justify-center">
               {item?.isVegetarian === true || item?.isVeg === true || item?.veg === true ? (
                 <div className="w-2.5 h-2.5 border border-green-600 rounded-sm flex items-center justify-center">
                   <div className="w-1 h-1 rounded-full bg-green-600" />
                 </div>
               ) : (
                 <div className="w-2.5 h-2.5 border border-red-700 rounded-sm flex items-center justify-center">
                   <div className="w-1 h-1 rounded-full bg-red-700" />
                 </div>
               )}
            </div>
          </div>

          {/* Content below */}
          <div className="p-2 flex-1 flex flex-col justify-between gap-1 pb-10">
            <div className="space-y-0.5">
              <h3 className="font-bold line-clamp-2 text-[12px] leading-snug" title={item?.name}>
                {item?.name
                  ? item.name.charAt(0).toUpperCase() + item.name.slice(1)
                  : ''}
              </h3>
              {discount && (
                <span className="inline-block text-[8px] font-bold px-1 py-0.2 rounded border border-gray-100 bg-gray-50 text-gray-600">
                  {discount}% OFF
                </span>
              )}
            </div>

            {/* Price & Rating */}
            <div className="flex items-center justify-between gap-1 flex-wrap mt-0.5">
              <div className="flex flex-col">
                <p className="font-bold text-gray-900 text-[11px]">
                  {hasPortions
                    ? `${MoneyUtils.format(item?.price ?? 0)}+`
                    : MoneyUtils.format(displayPrice ?? 0)}
                </p>
                {!hasPortions && showOffer && (
                  <p className="text-[9px] text-gray-400 line-through leading-none">
                    {MoneyUtils.format(item.price)}
                  </p>
                )}
              </div>
              {item?.rating && (
                <span className="flex items-center text-gray-500 text-[9px] font-medium">
                  <span className="text-yellow-400 mr-0.5">★</span>
                  {item.rating}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action widget positioned absolute bottom-right */}
        {isOrderingEnabled && (
          <div
            className="absolute right-2 bottom-2"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionWidget />
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="bg-white rounded-xl shadow-md overflow-hidden text-[12px] mt-2"
    >
      {/* Main card row */}
      <div onClick={handleCardClick} className="min-h-20 flex cursor-pointer relative">
        {/* Image */}
        <div className="relative w-1/5 flex-shrink-0 bg-gray-100 min-h-20">
          {item?.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item?.name || 'Menu item'}
              className={`w-full h-full object-cover absolute inset-0 transition-opacity duration-300 ${item?.isAvailable === false ? 'opacity-40 grayscale-[0.5]' : 'opacity-100'}`}
            />
          ) : (
            <div className="absolute inset-0 bg-gray-50 flex items-center justify-center text-gray-300">
              <Icon 
                name={/(drink|beverage|juice|coffee|tea|shake|smoothie|water)/i.test(item?.category || item?.name) ? 'Coffee' : 'Utensils'} 
                size={24} 
                strokeWidth={1.5}
              />
            </div>
          )}

          
          {/* Combo Ribbon Banner */}
          {item?.isCombo && (
              <div className="absolute top-0 left-0 bg-black text-white text-[9px] font-bold px-1.5 py-0.5 rounded-br-lg shadow-sm z-10">
                  COMBO
              </div>
          )}
        </div>

        {/* Content */}
        <div className={`flex flex-col justify-center gap-0.5 w-4/5 p-2 ${isOrderingEnabled ? 'pr-10' : 'pr-3'}`}>
          {/* Row 1: name + discount badge */}
          <div className="flex justify-between items-start gap-2">
            <h3 className="font-bold truncate text-[13px]" title={item?.name}>
              {item?.name
                ? item.name.charAt(0).toUpperCase() + item.name.slice(1)
                : ''}
            </h3>
            {discount && (
              <div className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 flex-shrink-0">
                {discount}% OFF
              </div>
            )}
          </div>

          {/* Row 2: Price + Veg Indicator + Rating */}
          <div className="flex items-center gap-2 mt-0.5">
            <div className="flex items-baseline gap-1.5">
              <p className="font-bold text-gray-900">
                {hasPortions
                  ? `From ${MoneyUtils.format(item?.price ?? 0)}`
                  : MoneyUtils.format(displayPrice ?? 0)}
              </p>
              {!hasPortions && showOffer && (
                <p className="text-[10px] text-gray-400 line-through">
                  {MoneyUtils.format(item.price)}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
                 {/* Veg/Non-Veg Indicator */}
                 {item?.isVegetarian === true || item?.isVeg === true || item?.veg === true ? (
                   <div className="w-3 h-3 border border-green-600 rounded-sm flex items-center justify-center flex-shrink-0">
                     <div className="w-1.5 h-1.5 rounded-full bg-green-600" />
                   </div>
                 ) : (
                   <div className="w-3 h-3 border border-red-700 rounded-sm flex items-center justify-center flex-shrink-0">
                     <div className="w-1.5 h-1.5 rounded-full bg-red-700" />
                   </div>
                 )}

                 {/* Vegan Indicator */}
                 {(item?.isVegan === true || item?.vegan === true) && (
                   <div className="flex items-center text-green-600 flex-shrink-0" title="Vegan">
                     <Icon name="Leaf" size={12} color="#16a34a" />
                   </div>
                 )}

                {/* Rating */}
                {item?.rating && (
                  <span className="flex items-center text-gray-500 text-[10px] font-medium">
                    <span className="text-yellow-400 mr-0.5">★</span>
                    {item.rating}
                  </span>
                )}
            </div>
          </div>

          {/* Row 3: Description / Combo Details / Availability */}
          <div className="mt-0.5 min-h-[14px]">
            {item?.isAvailable === false ? (
              <p className="text-[10px] font-bold text-red-600 uppercase tracking-wide">
                Out of Stock
              </p>
            ) : item?.isCombo && item?.comboComponents ? (
              <p className="text-[10px] text-gray-500 line-clamp-1 italic leading-tight">
                {item.comboComponents.map(c => `${c.quantity || 1} x ${c.name}`).join(', ')}
              </p>
            ) : item?.description ? (
              <p className="text-[10px] text-gray-400 line-clamp-1 leading-tight">
                {item.description}
              </p>
            ) : null}
          </div>
        </div>

        {/* Action widget — absolutely centered on the right edge */}
        {isOrderingEnabled && (
          <div
            className="absolute right-3 top-1/2 -translate-y-1/2"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionWidget />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MenuItemCard;
